import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import "./PasesDeSalida.css";

import GenerarPaseDeSalida from "./GenerarPaseDeSalida";
import ModalCreaCarpeta from "./Modal";
import ListeaPases from "./ListeaPases";
import CadetesBuscadorPresente from "./CadetesBuscadorPresentes";

import ObtieneDatosCookies from "../tools/obtieneDatosCookiesUsuario";
import comprueboPermisos from "../tools/comprueboPermisos";
import socket from "../tools/socke";

const PasesDeSalida = () => {
  const Usuario = ObtieneDatosCookies();

  const [pasesPorSacar, setPasesPorSacar] = useState([]);
  const [pasesPorVolver, setPasesPorVolver] = useState([]);

  const [modalCreaPase, setModalCreaPase] = useState(false);
  const [cadeteSeleccionado, setCadeteSeleccionado] = useState(null);

  const [cargandoPendientes, setCargandoPendientes] = useState(true);
  const [cargandoActivos, setCargandoActivos] = useState(true);

  const [error, setError] = useState("");

  const puedeCrear = comprueboPermisos(
    Usuario?.funcion,
    "Crea Pases"
  );

  // ============================================================
  // CLIENTE
  // ============================================================

  const crearCliente = () => {
    const token = btoa(
      `${process.env.REACT_APP_KEY}:${process.env.REACT_APP_PASS}`
    );

    return axios.create({
      baseURL: `${process.env.REACT_APP_BASEURL}NCadetes/`,
      headers: {
        Authorization: `Basic ${token}`,
      },
    });
  };

  // ============================================================
  // CONSULTA SEGÚN SEDE
  // ============================================================

  const crearConsulta = (estado) => {
    if (Number(Usuario?.sedeID) === 1) {
      return {
        estado,
      };
    }

    return {
      sede:
        Number(Usuario?.sedeID) === 2
          ? "Rosario"
          : "Recreo",
      estado,
    };
  };

  // ============================================================
  // PENDIENTES
  // ============================================================

  const obtienePasesPorSacar = useCallback(async () => {
    try {
      setCargandoPendientes(true);
      setError("");

      const client = crearCliente();

      const response = await client.post(
        "obtienePasesDeSalida/",
        crearConsulta("Pendiente")
      );

      setPasesPorSacar(
        Array.isArray(response?.data?.Pases)
          ? response.data.Pases
          : []
      );
    } catch (error) {
      console.error(
        "Error obteniendo pases pendientes:",
        error
      );

      setPasesPorSacar([]);

      setError(
        "No se pudieron obtener los pases pendientes."
      );
    } finally {
      setCargandoPendientes(false);
    }
  }, [Usuario?.sedeID]);

  // ============================================================
  // ACTIVOS / POR VOLVER
  // ============================================================

  const obtienePasesPorVolver = useCallback(async () => {
    try {
      setCargandoActivos(true);

      const client = crearCliente();

      const response = await client.post(
        "obtienePasesDeSalida/",
        crearConsulta("Activo")
      );

      setPasesPorVolver(
        Array.isArray(response?.data?.Pases)
          ? response.data.Pases
          : []
      );
    } catch (error) {
      console.error(
        "Error obteniendo pases activos:",
        error
      );

      setPasesPorVolver([]);

      setError(
        "No se pudieron obtener los pases activos."
      );
    } finally {
      setCargandoActivos(false);
    }
  }, [Usuario?.sedeID]);

  // ============================================================
  // REFRESCAR
  // ============================================================

  const refrescar = useCallback(() => {
    obtienePasesPorSacar();
    obtienePasesPorVolver();
  }, [
    obtienePasesPorSacar,
    obtienePasesPorVolver,
  ]);

  // ============================================================
  // INICIAR PASE
  // ============================================================

  const inicioPase = (cadete) => {
    setCadeteSeleccionado(cadete);
    setModalCreaPase(true);
  };

  // ============================================================
  // CERRAR MODAL
  // ============================================================

  const manejaCerradoPasesForm = () => {
    setModalCreaPase(false);
    setCadeteSeleccionado(null);

    refrescar();
  };

  // ============================================================
  // ANULAR
  // ============================================================

  const handleAnular = async (pase) => {
    try {
      const client = crearCliente();

      const payload = {
        usuarioSacoId: Usuario.identificacion,
        estado: "Anulado",
        fechaHoraUsuarioSaco:
          new Date().toISOString(),
      };

      const response = await client.post(
        `actualizaPaseDeSalida/${pase.id}`,
        payload
      );

      if (response?.data) {
        socket.emit(
          "creaPresenteCadete",
          ""
        );

        await Swal.fire({
          title: "Pase anulado",
          text: "El pase de salida del cadete fue anulado correctamente.",
          icon: "success",
          confirmButtonText: "Aceptar",
        });

        refrescar();
      }
    } catch (error) {
      console.error(
        "Error anulando pase:",
        error
      );

      Swal.fire(
        "Error",
        "No se pudo anular el pase de salida.",
        "error"
      );
    }
  };

  // ============================================================
  // SACAR CADETE
  // ============================================================

  const SacarPaseSalida = async (pase) => {
    const formatearFechaHora = (timestamp) => {
      const date = new Date(timestamp);

      return date.toLocaleString(
        "es-AR",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    };

    const apellido =
      pase?.cadete?.cadeteApellido || "";

    const nombre =
      pase?.cadete?.cadeteNombre || "";

    const nombreCompleto =
      `${apellido} ${nombre}`.trim();

    const resultado = await Swal.fire({
      title: "Autorizar salida",
      html: `
        <div style="text-align:left;line-height:1.6">
          Está por autorizar la salida de
          <strong>${nombreCompleto}</strong>.
          <br><br>
          El regreso previsto es:
          <strong>
            ${formatearFechaHora(
              pase.fechaHoraRegreso
            )}
          </strong>.
          <br><br>
          Recuerde informar al cadete que cualquier novedad deberá ser comunicada por el sistema de Gestión de Cadetes.
        </div>
      `,
      icon: "warning",

      showCancelButton: true,
      showDenyButton: true,

      confirmButtonText:
        "Autorizar salida",

      denyButtonText:
        "Anular pase",

      cancelButtonText:
        "Cancelar",

      confirmButtonColor:
        "#315f64",
    });

    if (resultado.isConfirmed) {
      try {
        const client = crearCliente();

        const payload = {
          usuarioSacoId:
            Usuario.identificacion,

          estado: "Activo",

          fechaHoraUsuarioSaco:
            new Date().toISOString(),
        };

        const response =
          await client.post(
            `actualizaPaseDeSalida/${pase.id}`,
            payload
          );

        if (response) {
          socket.emit(
            "creaPresenteCadete",
            []
          );

          await Swal.fire({
            title:
              "Salida autorizada",
            text:
              "El cadete ha sido autorizado a salir.",
            icon: "success",
            confirmButtonText:
              "Aceptar",
          });

          refrescar();
        }
      } catch (error) {
        console.error(
          "Error autorizando salida:",
          error
        );

        Swal.fire(
          "Error",
          "No se pudo autorizar la salida.",
          "error"
        );
      }

      return;
    }

    if (resultado.isDenied) {
      handleAnular(pase);
    }
  };

  // ============================================================
  // VER OBSERVACION
  // ============================================================

  const mostrarObservacion = (pase) => {
    Swal.fire({
      title: "Información del pase",

      html: `
        <div style="text-align:left">
          <strong>Motivo / Observación</strong>
          <div style="
            margin-top:10px;
            padding:12px;
            border-radius:10px;
            background:#f5f7f8;
            color:#344054;
          ">
            ${
              pase?.observacion ||
              "Sin observaciones"
            }
          </div>
        </div>
      `,

      confirmButtonText:
        "Cerrar",

      confirmButtonColor:
        "#315f64",
    });
  };

  // ============================================================
  // SOCKET
  // ============================================================

  useEffect(() => {
    refrescar();

    const handlePresenteCadete =
      () => {
        refrescar();
      };

    socket.on(
      "listaPresentes",
      handlePresenteCadete
    );

    return () => {
      socket.off(
        "listaPresentes",
        handlePresenteCadete
      );
    };
  }, [refrescar]);

  // ============================================================
  // PROMOCION DEL CADETE
  // ============================================================

  const obtenerPromocion = (
    cadete
  ) => {
    return (
      cadete?.Promocions?.[0]?.id ||
      null
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="ps-v2-page">
      <div className="ps-v2-container">

        {/* ==================================================== */}
        {/* HEADER */}
        {/* ==================================================== */}

        <section className="ps-v2-header">
          <div>
            <span className="ps-v2-eyebrow">
              GESTIÓN DE CADETES · ISeP
            </span>

            <h1 className="ps-v2-title">
              Pases de Salida
            </h1>

            <p className="ps-v2-subtitle">
              Generación, autorización y seguimiento
              de salidas temporales de cadetes.
            </p>
          </div>

          <button
            type="button"
            className="ps-v2-refresh"
            onClick={refrescar}
            disabled={
              cargandoPendientes ||
              cargandoActivos
            }
          >
            <span>↻</span>

            {cargandoPendientes ||
            cargandoActivos
              ? "Actualizando..."
              : "Actualizar"}
          </button>
        </section>

        {/* ==================================================== */}
        {/* ERROR */}
        {/* ==================================================== */}

        {error && (
          <div className="ps-v2-error">
            <div>
              <strong>
                No se pudo actualizar toda
                la información
              </strong>

              <span>{error}</span>
            </div>

            <button
              type="button"
              onClick={() => setError("")}
            >
              ×
            </button>
          </div>
        )}

        {/* ==================================================== */}
        {/* CREAR */}
        {/* ==================================================== */}

        {puedeCrear && (
          <section className="ps-v2-create-card">
            <div className="ps-v2-create-info">
              <div className="ps-v2-create-icon">
                +
              </div>

              <div>
                <h2>
                  Nuevo pase de salida
                </h2>

                <p>
                  Busque un cadete presente
                  para iniciar una nueva
                  autorización de salida.
                </p>
              </div>
            </div>

            <div className="ps-v2-search-box">
              <span className="ps-v2-search-label">
                Buscar cadete presente
              </span>

              <CadetesBuscadorPresente
                devuelvo={(cadete) =>
                  inicioPase(cadete)
                }
              />
            </div>
          </section>
        )}

        {/* ==================================================== */}
        {/* RESUMEN */}
        {/* ==================================================== */}

        <section className="ps-v2-summary">

          <div className="ps-v2-stat-card ps-v2-stat-pending">
            <div className="ps-v2-stat-icon">
              ↗
            </div>

            <div>
              <span className="ps-v2-stat-label">
                Por sacar
              </span>

              <strong>
                {cargandoPendientes
                  ? "..."
                  : pasesPorSacar.length}
              </strong>

              <small>
                Autorizaciones pendientes
              </small>
            </div>
          </div>

          <div className="ps-v2-stat-card ps-v2-stat-active">
            <div className="ps-v2-stat-icon">
              ↩
            </div>

            <div>
              <span className="ps-v2-stat-label">
                Por volver
              </span>

              <strong>
                {cargandoActivos
                  ? "..."
                  : pasesPorVolver.length}
              </strong>

              <small>
                Cadetes fuera del ISeP
              </small>
            </div>
          </div>

        </section>

        {/* ==================================================== */}
        {/* PENDIENTES */}
        {/* ==================================================== */}

        <section className="ps-v2-section">

          <div className="ps-v2-section-header">
            <div className="ps-v2-section-title-group">

              <div className="ps-v2-section-indicator ps-v2-section-indicator-pending" />

              <div>
                <h2>
                  Pases pendientes de salida
                </h2>

                <p>
                  Autorizaciones generadas que
                  todavía no registraron la salida
                  efectiva del cadete.
                </p>
              </div>

            </div>

            <span className="ps-v2-counter ps-v2-counter-pending">
              {pasesPorSacar.length}
            </span>
          </div>

          <div className="ps-v2-section-content">

            {cargandoPendientes ? (
              <div className="ps-v2-loading">
                <div className="ps-v2-spinner" />

                <span>
                  Obteniendo pases pendientes...
                </span>
              </div>
            ) : pasesPorSacar.length >
              0 ? (
              <div className="ps-v2-list-wrapper">

                <ListeaPases
                  pases={pasesPorSacar}
                  paso={1}
                  onClick={(pase) =>
                    SacarPaseSalida(pase)
                  }
                />

              </div>
            ) : (
              <div className="ps-v2-empty">

                <div className="ps-v2-empty-icon">
                  ✓
                </div>

                <strong>
                  Sin pases pendientes
                </strong>

                <span>
                  No existen cadetes con pases
                  aguardando autorización de salida.
                </span>

              </div>
            )}

          </div>
        </section>

        {/* ==================================================== */}
        {/* ACTIVOS */}
        {/* ==================================================== */}

        <section className="ps-v2-section">

          <div className="ps-v2-section-header">

            <div className="ps-v2-section-title-group">

              <div className="ps-v2-section-indicator ps-v2-section-indicator-active" />

              <div>
                <h2>
                  Cadetes por regresar
                </h2>

                <p>
                  Pases que ya registraron salida y
                  permanecen activos.
                </p>
              </div>

            </div>

            <span className="ps-v2-counter ps-v2-counter-active">
              {pasesPorVolver.length}
            </span>

          </div>

          <div className="ps-v2-section-content">

            {cargandoActivos ? (
              <div className="ps-v2-loading">
                <div className="ps-v2-spinner" />

                <span>
                  Obteniendo pases activos...
                </span>
              </div>
            ) : pasesPorVolver.length >
              0 ? (
              <div className="ps-v2-list-wrapper">

                <ListeaPases
                  pases={pasesPorVolver}
                  paso={2}
                  onClick={(pase) =>
                    mostrarObservacion(pase)
                  }
                />

              </div>
            ) : (
              <div className="ps-v2-empty">

                <div className="ps-v2-empty-icon">
                  ✓
                </div>

                <strong>
                  Todos los cadetes se encuentran
                  dentro del establecimiento
                </strong>

                <span>
                  No existen pases de salida activos
                  pendientes de regreso.
                </span>

              </div>
            )}

          </div>
        </section>

      </div>

      {/* ====================================================== */}
      {/* MODAL NUEVO PASE */}
      {/* ====================================================== */}

      {modalCreaPase &&
        cadeteSeleccionado && (
          <ModalCreaCarpeta
            isOpen={modalCreaPase}
            onClose={
              manejaCerradoPasesForm
            }
          >
            <div className="ps-v2-modal-content">

              <div className="ps-v2-modal-header">

                <span className="ps-v2-modal-eyebrow">
                  NUEVO PASE
                </span>

                <h3>
                  Crear Pase de Salida
                </h3>

                <div className="ps-v2-modal-person">

                  <div className="ps-v2-avatar">
                    {cadeteSeleccionado
                      ?.cadeteApellido
                      ?.charAt(0)
                      ?.toUpperCase() ||
                      cadeteSeleccionado
                        ?.cadeteNombre
                        ?.charAt(0)
                        ?.toUpperCase() ||
                      "C"}
                  </div>

                  <div>
                    <strong>
                      {`${cadeteSeleccionado?.cadeteApellido || ""} ${cadeteSeleccionado?.cadeteNombre || ""}`.trim()}
                    </strong>

                    <span>
                      DNI{" "}
                      {cadeteSeleccionado?.cadeteDni ||
                        "-"}
                    </span>
                  </div>

                </div>
              </div>

              <div className="ps-v2-modal-body">

                <GenerarPaseDeSalida
                  datos={{
                    cadeteDni:
                      cadeteSeleccionado.cadeteDni,

                    cadeteSede:
                      cadeteSeleccionado.cadeteSede,

                    cadetePromocion:
                      obtenerPromocion(
                        cadeteSeleccionado
                      ),
                  }}

                  onClose={
                    manejaCerradoPasesForm
                  }
                />

              </div>
            </div>
          </ModalCreaCarpeta>
        )}
    </div>
  );
};

export default PasesDeSalida;